import React from 'react';
import { StyleProp, View, ViewStyle, Pressable } from 'react-native';
import MyAppText from './MyAppText';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { Controller, UseFormReturn } from 'react-hook-form';

type MyCheckboxParameters<T> = {
    options: { label: string; value: T; }[];
    editable?: boolean;
    style?: StyleProp<ViewStyle>;
    labelFontSize?: number;
} & ({
    form?: undefined;
    fieldName?: undefined;
    selected: string | boolean | number | null;
    setSelected: (value: T) => void;
} | {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    form: UseFormReturn<any, any, any>;
    fieldName: string;
    selected?: undefined;
    setSelected?: undefined;
});

function RadioGroup<T>({
    options,
    selected,
    onSelected,
    editable,
    labelFontSize,
    style,
}: {
    options: MyCheckboxParameters<T>['options'];
    selected: unknown;
    onSelected: (value: T) => void;
    editable: boolean;
    labelFontSize?: number;
    style?: StyleProp<ViewStyle>;
}) {
    const { styles } = useStyles(stylesheet);

    return (
        <View style={!style ? styles.radioContainer : style}>
            {options.map((obj, i) => (
                <Pressable
                    key={i}
                    style={styles.option}
                    onPress={() => editable && onSelected(obj.value)}
                    disabled={!editable}
                >
                    <View style={styles.outerCircle}>
                        {selected === obj.value && (
                            <View style={styles.innerCircle} />
                        )}
                    </View>
                    <MyAppText style={styles.label(labelFontSize ?? 16)}>
                        {obj.label}
                    </MyAppText>
                </Pressable>
            ))}
        </View>
    );
}

export default function MyRadio<T>({ options, selected, setSelected, style, labelFontSize, editable = true, form, fieldName }: MyCheckboxParameters<T>) {
    const { styles } = useStyles(stylesheet);

    if (form && fieldName) {
        return (
            <View style={!style ? styles.container : style}>
                <Controller
                    control={form.control}
                    name={fieldName}
                    render={({ field }) => (
                        <RadioGroup
                            options={options}
                            selected={field.value}
                            onSelected={(value) => editable && field.onChange(value)}
                            editable={editable}
                            labelFontSize={labelFontSize}
                            style={style}
                        />
                    )}
                />
            </View>
        );
    } else if (selected !== undefined && setSelected !== undefined) {
        return (
            <View style={!style ? styles.container : style}>
                <RadioGroup
                    options={options}
                    selected={selected}
                    onSelected={setSelected}
                    editable={editable}
                    labelFontSize={labelFontSize}
                    style={style}
                />
            </View>
        );
    }
}

const stylesheet = createStyleSheet(theme => ({
    container: {
        flexDirection: 'row',
        alignItems: 'center',
        columnGap: 10,
    },
    radioContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        flexWrap: 'wrap',
        flex: 1,
    },
    option: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        cursor: 'pointer',
    },
    outerCircle: {
        width: 20,
        height: 20,
        borderRadius: 10,
        borderWidth: 2,
        borderColor: theme.colors.iconColor,
        alignItems: 'center',
        justifyContent: 'center'
    },
    innerCircle: {
        width: 10,
        height: 10,
        borderRadius: 5,
        backgroundColor: theme.colors.iconColor
    },
    label: (fontSize: number) => ({
        color: theme.colors.labelColor,
        fontSize,
        height: 25,
        fontWeight: '400'
    }),
}));
